import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, ListGroup, Spinner } from 'react-bootstrap';
import { FaSchool, FaTimes } from 'react-icons/fa';
import { config } from '../constants.js';
import './SchoolSelector.scss';

const API_URL = config.url.API_URL;
const MIN_QUERY_LENGTH = 5;
const DEBOUNCE_MS = 280;

function schoolValuesEqual(a, b) {
  return String(a?.schoolId || '') === String(b?.schoolId || '')
    && String(a?.schoolName || '') === String(b?.schoolName || '');
}

export default function SchoolSelector({
  value = { schoolId: null, schoolName: '' },
  onChange,
  required = false,
  label = 'School',
  placeholder = 'Start typing your school…',
  id = 'schoolSelector',
  disabled = false,
  highlightEmpty = false,
  onSave,
  saving = false,
  savedValue,
}) {
  const selectedId = value?.schoolId || null;
  const selectedName = value?.schoolName || '';
  const isManual = !selectedId && Boolean(selectedName);
  const hasSavedValue = savedValue !== undefined;
  const committed = hasSavedValue
    ? { schoolId: savedValue?.schoolId || null, schoolName: savedValue?.schoolName || '' }
    : { schoolId: selectedId, schoolName: selectedName };
  const isDirty = hasSavedValue && !schoolValuesEqual(
    { schoolId: selectedId, schoolName: selectedName },
    committed
  );
  const showSave = Boolean(onSave) && isDirty;
  const showCommitted = Boolean(onSave) && !isDirty;

  const [query, setQuery] = useState(isManual || !selectedId ? selectedName : '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [manualMode, setManualMode] = useState(isManual);
  const [editing, setEditing] = useState(false);
  const [searching, setSearching] = useState(false);
  const [pendingClear, setPendingClear] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  const handleCancelSearch = () => {
    setOpen(false);
    if (selectedId && editing) {
      setEditing(false);
      setQuery('');
      setResults([]);
      setError('');
    } else if (searching) {
      setSearching(false);
      setQuery('');
      setResults([]);
      setError('');
    }
  };

  useEffect(() => {
    setManualMode(!selectedId && Boolean(selectedName));
    setEditing(false);
    setPendingClear(false);
    if (selectedId) {
      setSearching(false);
      setQuery('');
      setResults([]);
      setOpen(false);
    } else {
      setQuery(selectedName);
    }
  }, [selectedId, selectedName]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        handleCancelSearch();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedId, editing, searching]);

  useEffect(() => {
    if (manualMode || (selectedId && !editing) || disabled) {
      return undefined;
    }

    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setLoading(false);
      return undefined;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(
          `${API_URL}/school/search?q=${encodeURIComponent(trimmed)}`,
          { credentials: 'include' }
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.message || 'Unable to search schools.');
        }
        setResults(data.schools || []);
        setOpen(true);
      } catch (searchError) {
        setError(searchError.message || 'Unable to search schools.');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, manualMode, selectedId, editing, disabled]);

  const emitChange = (next) => {
    onChange?.({
      schoolId: next.schoolId || null,
      schoolName: next.schoolName || '',
      city: next.city || null,
      state: next.state || null,
    });
  };

  const handleSelect = (school) => {
    setEditing(false);
    emitChange({
      schoolId: school.id,
      schoolName: school.name,
      city: school.city,
      state: school.state,
    });
  };

  const handleChangeSelection = () => {
    setEditing(true);
    setQuery('');
    setManualMode(false);
    setResults([]);
    setOpen(false);
  };

  const restoreCommitted = () => {
    setEditing(false);
    setSearching(false);
    setManualMode(false);
    setQuery('');
    setResults([]);
    setOpen(false);
    setPendingClear(false);
    emitChange({
      schoolId: committed.schoolId,
      schoolName: committed.schoolName,
    });
  };

  const hasCommittedSchool = Boolean(committed.schoolId || committed.schoolName);

  const handleCancelSelection = () => {
    restoreCommitted();
  };

  const handleStartSearch = () => {
    setSearching(true);
    setQuery('');
    setManualMode(false);
    setResults([]);
    setOpen(false);
  };

  const handleStartClear = () => {
    setEditing(false);
    setSearching(false);
    setManualMode(false);
    setQuery('');
    setResults([]);
    setOpen(false);
    if (selectedId) {
      setPendingClear(true);
    }
  };

  const handleCancelClear = () => {
    setPendingClear(false);
  };

  const handleSaveClear = () => {
    if (onSave) {
      onSave({ schoolId: null, schoolName: '' });
      return;
    }
    emitChange({ schoolId: null, schoolName: '' });
    setPendingClear(false);
  };

  const handleSaveSelection = () => {
    onSave?.();
  };

  const handleManualNameChange = (event) => {
    emitChange({
      schoolId: null,
      schoolName: event.target.value,
    });
  };

  const labelIcon = highlightEmpty ? (
    <FaSchool className="school-selector__stat-icon me-2" aria-hidden="true" />
  ) : null;

  if (pendingClear) {
    return (
      <Form.Group className="mb-3 school-selector school-selector--saved" controlId={id}>
        <div className="school-selector__selected school-selector__selected--saved">
          {labelIcon}
          <div className="school-selector__stat-text">
            {label && <span className="school-selector__stat-label">{label}:</span>}
            <span className="school-selector__stat-value school-selector__stat-value--placeholder">None yet</span>
          </div>
          <div className="school-selector__actions">
            <Button
              type="button"
              size="sm"
              className="school-selector__action-btn school-selector__action-btn--save"
              onClick={handleSaveClear}
              disabled={disabled || saving}
            >
              {saving ? (
                <Spinner animation="border" size="sm" role="status" aria-label="Saving" />
              ) : (
                'SAVE'
              )}
            </Button>
            <Button
              type="button"
              variant="outline-dark"
              size="sm"
              className="school-selector__action-btn school-selector__action-btn--cancel"
              onClick={handleCancelClear}
              disabled={disabled || saving}
            >
              CANCEL
            </Button>
          </div>
        </div>
      </Form.Group>
    );
  }

  if (selectedId && !editing) {
    return (
      <Form.Group
        className={`mb-3 school-selector${showCommitted ? ' school-selector--saved' : ''}`}
        controlId={id}
      >
        {label && !showCommitted && (
          <Form.Label className="d-flex align-items-center">
            {labelIcon}
            {label}
          </Form.Label>
        )}
        <div className={`school-selector__selected${showCommitted ? ' school-selector__selected--saved' : ''}${!highlightEmpty ? ' school-selector__selected--form' : ''}`}>
          {showCommitted ? (
            <>
              {labelIcon}
              <div className="school-selector__stat-text">
                {label && <span className="school-selector__stat-label">{label}:</span>}
                <span className="school-selector__stat-value">{selectedName}</span>
              </div>
            </>
          ) : (
            <div>
              <div className="school-selector__selected-name">{selectedName}</div>
            </div>
          )}
          <div className="school-selector__actions">
            <Button
              type="button"
              variant={showSave || saving ? 'primary' : 'outline-dark'}
              size="sm"
              className={`school-selector__action-btn${showSave || saving ? ' school-selector__action-btn--save' : ' school-selector__action-btn--cancel'}`}
              onClick={showSave || saving ? handleSaveSelection : handleChangeSelection}
              disabled={disabled || saving}
              aria-label={showSave || saving ? undefined : 'Edit school'}
            >
              {saving ? (
                <Spinner animation="border" size="sm" role="status" aria-label="Saving" />
              ) : showSave ? (
                'SAVE'
              ) : (
                <>
                  Edit
                  <FaTimes className="school-selector__action-btn-x" aria-hidden="true" />
                </>
              )}
            </Button>
            {showSave && (
              <Button
                type="button"
                variant="outline-dark"
                size="sm"
                className="school-selector__action-btn school-selector__action-btn--cancel"
                onClick={handleCancelSelection}
                disabled={disabled || saving}
              >
                CANCEL
              </Button>
            )}
          </div>
        </div>
      </Form.Group>
    );
  }

  if (highlightEmpty && !selectedId && !selectedName && !searching && !manualMode) {
    return (
      <Form.Group className="mb-3 school-selector school-selector--saved" controlId={id}>
        <div className="school-selector__selected school-selector__selected--saved">
          {labelIcon}
          <div className="school-selector__stat-text">
            {label && <span className="school-selector__stat-label">{label}:</span>}
                <span className="school-selector__stat-value school-selector__stat-value--placeholder">None yet</span>
          </div>
          <div className="school-selector__actions">
            <Button
              type="button"
              size="sm"
              className="school-selector__action-btn school-selector__action-btn--select"
              onClick={handleStartSearch}
              disabled={disabled}
            >
              Add
            </Button>
          </div>
        </div>
      </Form.Group>
    );
  }

  if (manualMode) {
    const canSaveManual = Boolean(onSave) && Boolean(selectedName.trim()) && isDirty;
    return (
      <Form.Group className="mb-3 school-selector" controlId={id}>
        {label && (
          <Form.Label className="d-flex align-items-center">
            {labelIcon}
            {label}
          </Form.Label>
        )}
        <div className="school-selector__search-row">
          <div className="school-selector__input-wrap">
            <Form.Control
              type="text"
              value={selectedName}
              onChange={handleManualNameChange}
              maxLength={200}
              required={required}
              disabled={disabled}
              placeholder="Enter your school name"
              autoComplete="organization"
            />
          </div>
          {onSave && (
            <div className="school-selector__actions">
              <Button
                type="button"
                size="sm"
                className="school-selector__action-btn school-selector__action-btn--save"
                onClick={handleSaveSelection}
                disabled={disabled || saving || !canSaveManual}
              >
                {saving ? (
                  <Spinner animation="border" size="sm" role="status" aria-label="Saving" />
                ) : (
                  'SAVE'
                )}
              </Button>
              <Button
                type="button"
                variant="outline-dark"
                size="sm"
                className="school-selector__action-btn school-selector__action-btn--cancel"
                onClick={restoreCommitted}
                disabled={disabled || saving}
              >
                CANCEL
              </Button>
            </div>
          )}
        </div>
        <Button
          type="button"
          variant="link"
          className="school-selector__toggle px-0"
          onClick={() => {
            if (hasSavedValue && hasCommittedSchool) {
              restoreCommitted();
              return;
            }
            setManualMode(false);
            setSearching(true);
            setQuery('');
            setResults([]);
            setOpen(false);
            emitChange({ schoolId: null, schoolName: '' });
          }}
          disabled={disabled}
        >
          Search the school directory instead
        </Button>
      </Form.Group>
    );
  }

  const showEmptyHint = highlightEmpty && !loading && !query.trim();

  return (
    <div className={`mb-3 school-selector${showEmptyHint ? ' school-selector--empty' : ''}`} ref={wrapperRef}>
      <Form.Group controlId={id}>
        {label && (
          <Form.Label className="d-flex align-items-center">
            {labelIcon}
            {label}
          </Form.Label>
        )}
        <div className="school-selector__search-row">
        <div className="school-selector__input-wrap">
        <Form.Control
          type="text"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            if (results.length > 0) {
              setOpen(true);
            }
          }}
          placeholder={placeholder}
          required={required && !selectedName}
          disabled={disabled}
          autoComplete="off"
        />
        {loading && (
          <Spinner
            animation="border"
            size="sm"
            className="school-selector__spinner"
            role="status"
          />
        )}
      </div>
        {(searching || editing) && (
          <Button
            type="button"
            variant="link"
            className="school-selector__dismiss p-0"
            onClick={handleCancelSearch}
            disabled={disabled}
            aria-label="Cancel"
          >
            <FaTimes aria-hidden="true" />
          </Button>
        )}
      </div>
      {error && <div className="text-danger small mt-1">{error}</div>}
      {open && results.length > 0 && (
        <ListGroup className="school-selector__results">
          {results.map((school) => (
            <ListGroup.Item
              action
              key={school.id}
              onClick={() => handleSelect(school)}
            >
              <div className="school-selector__result-name">{school.name}</div>
              <div className="school-selector__result-meta">
                {[school.city, school.state].filter(Boolean).join(', ')}
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
      {open && !loading && query.trim().length >= MIN_QUERY_LENGTH && results.length === 0 && !error && (
        <div className="school-selector__empty text-muted small mt-1">
          No matching schools.
        </div>
      )}
      <div className="school-selector__toggles">
        <Button
          type="button"
          variant="link"
          className="school-selector__toggle px-0"
          onClick={() => {
            setManualMode(true);
            setOpen(false);
          }}
          disabled={disabled}
        >
          School not listed? Type it instead
        </Button>
        {!required && highlightEmpty && (
          <Button
            type="button"
            variant="link"
            className="school-selector__toggle school-selector__toggle--end px-0"
            onClick={handleStartClear}
            disabled={disabled}
          >
            Don&apos;t list a school
          </Button>
        )}
      </div>
      </Form.Group>
    </div>
  );
}
